import React from 'react'
import { compact } from 'lodash'
import { Link as RouterLink } from 'react-router-dom'
import { graphql, useFragment } from 'react-relay/hooks'
import { Dropdown, LinkButton, Icon } from 'elements'
import { useAnalytics, useStorage, useAuth } from 'hooks'
import useReportArchive from '../hooks/useReportArchive'
import useReportRestore from '../hooks/useReportRestore'
import useReportRetry from '../hooks/useReportRetry'
import useRetryReportWithIntegration from '../hooks/useRetryReportWithIntegration'
import useReportDelete from '../hooks/useReportDelete'
import { downloadFile } from '../utils/download'
import { formatReportForExport } from '../export/formatters'

const ANOMALY_REPORT_KIND = 'scrutinize:json:anomaly-report:1'

async function handleReportExport({
  anomalyReportId,
  storage,
  reportName,
  setWorking,
  configSections,
}) {
  const working = { label: 'Downloading...', progress: 0 }

  setWorking(working)

  const { exportReport } = await import('../export/exportReport')
  setWorking({ ...working, progress: 20 })

  const output = await storage.get(anomalyReportId)
  setWorking({ ...working, progress: 60 })

  const reportExportData = formatReportForExport(output, configSections)
  setWorking({ ...working, progress: 80 })

  const blob = await exportReport(reportExportData)
  setWorking({ ...working, progress: 90 })

  downloadFile(`Scrutinize - ${reportName}.xlsx`, blob)
  setWorking({ ...working, progress: 100 })

  setWorking(null)
}

function getReportType(report) {
  if (!report) {
    return null
  }

  const { reportUserFiles, reportIntegrations } = report

  const sourceFiles = reportUserFiles.nodes.filter((file) => file.type === 'SOURCE')

  if (
    reportIntegrations?.nodes?.length ||
    sourceFiles.every((file) => file.userFile.kind.includes('json'))
  ) {
    return 'integration'
  }

  if (sourceFiles.length) {
    return 'upload'
  }

  return null
}

export default function ReportContextMenu(props) {
  const { currentOrg } = useAuth()
  const storage = useStorage()
  const analytics = useAnalytics()
  const [archiveReport] = useReportArchive()
  const [restoreReport] = useReportRestore()
  const [retryReport] = useReportRetry()
  const [retryReportWithIntegration] = useRetryReportWithIntegration()
  const [deleteReport] = useReportDelete()

  const report = useFragment(
    graphql`
      fragment ReportContextMenu_report on Report {
        rowId
        id
        name
        status
        deletedAt
        config
        company {
          id
        }
        reportUserFiles {
          nodes {
            type
            userFile {
              id
              rowId
              kind
              name
              key
            }
          }
        }
        reportIntegrations {
          nodes {
            integration {
              id
              owner {
                id
              }
            }
          }
        }
      }
    `,
    props.report,
  )

  if (!report) {
    return null
  }

  const anomalyReportFile = report.reportUserFiles.nodes.find(
    (file) => file.userFile.kind === ANOMALY_REPORT_KIND,
  )

  const { status, reportIntegrations } = report

  const isSuccessful = status === 'SUCCESS'
  const isUnsuccessful = status === 'ERROR'
  const integration = reportIntegrations?.nodes?.[0]?.integration

  const reportType = getReportType(report)

  const actionOptions = compact([
    isSuccessful
      ? {
          label: 'View',
          as: RouterLink,
          to: `/orgs/${currentOrg.slug}/companies/${report.company.id}/reports/${report.id}`,
        }
      : null,
    isSuccessful
      ? {
          label: 'Download',
          onSelect: () => {
            handleReportExport({
              anomalyReportId: anomalyReportFile.userFile.rowId,
              storage,
              reportName: report.name,
              setWorking: props.setWorking,
              configSections: report.config?.sections,
            })
            analytics.track('Report Downloaded', { reportId: report.rowId })
          },
        }
      : null,
    isSuccessful || isUnsuccessful
      ? {
          label: 'Refresh',
          kind: 'normal',
          onSelect: () => {
            analytics.track('Report Retried', { reportId: report.rowId })

            if (reportType === 'integration') {
              return retryReportWithIntegration({
                reportId: report.id,
                integrationId: integration.id,
              })
            } else {
              return retryReport({ reportId: report.id })
            }
          },
        }
      : null,
    {
      label: report.deletedAt ? 'Restore' : 'Archive',
      kind: report.deletedAt ? 'normal' : 'danger',
      borderStyle: 'none',
      onSelect: () => {
        if (report.deletedAt) {
          analytics.track('Report Restored', { reportId: report.rowId })
          return restoreReport({ reportId: report.id })
        }

        analytics.track('Report Archived', { reportId: report.rowId })
        return archiveReport({ reportId: report.id })
      },
    },
    report.deletedAt && status === 'ERROR'
      ? {
          label: 'Delete Permanently',
          kind: 'danger',
          onSelect: () => {
            analytics.track('Report Deleted', { reportId: report.rowId })
            return deleteReport({ id: report.id })
          },
        }
      : null,
  ])

  return (
    <Dropdown
      position="bottom right"
      options={actionOptions}
      stopPropagation
      renderButton={() => (
        <LinkButton kind="transparent">
          <Icon name="dots-horizontal" color="neutral.2" />
        </LinkButton>
      )}
    />
  )
}
